/**
 * Scheduling UI Store — Zustand
 *
 * Owns ONLY client UI state. Server state (appointments, providers, slots)
 * is owned by React Query hooks in @/scheduling/services/*.
 *
 * Rule: Never put server data in this store. Never put UI state in React Query.
 *
 * Persistence: `selectedSpecialties` and `selectedProviderIds` survive page
 * reloads under a per-user localStorage key (mirrors Flutter's
 * `selected_specialities + userId` / `selected_providers + userId`
 * SharedPreferences keys). When the auth user changes (login/logout),
 * the store rehydrates from the new user's key automatically — so user A's
 * selections never leak into user B's session.
 *
 * `selectedDate`, `activeView`, and panel state are NOT persisted —
 * they reset to defaults on each load (matches Flutter).
 */
import { create } from 'zustand';
import {
  persist,
  type StateStorage,
} from 'zustand/middleware';
import { useAuthStore } from '@/auth/auth-store';
import { todayLocalYmd } from '@/shared/lib/ymd-local';

export type CalendarView = 'Day' | 'Week' | 'Month' | 'ByRoom' | 'ByDoctor';
export type PanelMode = 'create' | 'view' | 'edit' | 'block' | null;

/**
 * Inline edit/reschedule target for the calendar's left rail. Flutter parity
 * (`calendar_tab.dart:6433-6445`): the popup pencil / bottom Edit / Reschedule
 * open the appointment editor IN PLACE on the calendar — they do NOT navigate
 * to the full appointment screen (that's the 3-dots path). We keep only the
 * IDENTIFIERS here (server rule: no appointment object in this store); the
 * rail fetches the appointment via React Query (`useAppointmentDetails`).
 */
export interface EditPanelTarget {
  appointmentId: string;
  patientId: string;
  providerId: string;
  profileId: string;
  /** `edit` = full edit; `reschedule` = date/time-only constrained mode. */
  mode: 'edit' | 'reschedule';
}

interface SchedulingState {
  selectedDate: string;
  activeView: CalendarView;
  panelMode: PanelMode;
  selectedAppointmentId: string | null;
  selectedProviderIds: string[];
  /** Multi-select; empty list = "All" (no filter). */
  selectedSpecialties: string[];
  /**
   * Devices tab subtype filter (PROD-2007) — chambers / chairs / imaging…,
   * derived from the practice's devices. Empty = every device. Persisted like
   * the specialty chips; never mixes with them (the tabs never share a grid).
   */
  selectedDeviceSubtypes: string[];
  /**
   * Appointment status codes the user wants to see. Empty = no explicit
   * filter; the helper still hides Cancelled rows by default (toggle the
   * Cancelled pill to surface them). Non-empty = restrict to those codes.
   */
  selectedStatusCodes: string[];
  /**
   * Invoice statuses ("Draft" / "Issued" / "Balanced") — applied
   * client-side to narrow the appointments response. Empty = no filter.
   */
  selectedInvoiceStatuses: string[];
  /**
   * Practice location ids the user has toggled. Empty = no filter
   * (every location passes — matches Flutter where the buttons all
   * render gray on first load).
   */
  selectedLocationIds: string[];

  /**
   * Non-null when the inline calendar edit/reschedule rail is open. Set by the
   * appointment popup's pencil / Reschedule (Flutter's in-place edit), read by
   * FrameScheduling to mount CreateAppointmentPanel in edit/reschedule mode.
   * NOT persisted.
   */
  editTarget: EditPanelTarget | null;

  setSelectedDate: (date: string) => void;
  setActiveView: (view: CalendarView) => void;
  openPanel: (mode: PanelMode, appointmentId?: string | null) => void;
  closePanel: () => void;
  /** Open the inline edit/reschedule rail for an existing appointment. */
  openEditPanel: (target: EditPanelTarget) => void;
  /** Close the inline edit/reschedule rail. */
  closeEditPanel: () => void;
  setSelectedProviders: (ids: string[]) => void;
  toggleProvider: (id: string) => void;
  clearProviders: () => void;

  setSelectedSpecialties: (s: string[]) => void;
  toggleSpecialty: (s: string) => void;
  clearSpecialties: () => void;

  toggleDeviceSubtype: (s: string) => void;
  clearDeviceSubtypes: () => void;

  setSelectedStatusCodes: (codes: string[]) => void;
  toggleStatusCode: (code: string) => void;
  clearStatusCodes: () => void;

  setSelectedInvoiceStatuses: (codes: string[]) => void;
  toggleInvoiceStatus: (code: string) => void;
  clearInvoiceStatuses: () => void;

  toggleLocation: (id: string) => void;
  clearLocations: () => void;
}

const ANON_USER_ID = 'anonymous';

/** Resolve the storage suffix from the currently-known auth user. */
function currentUserKeySuffix(): string {
  return useAuthStore.getState().user?.userId ?? ANON_USER_ID;
}

/**
 * localStorage adapter that prefixes the persist `name` with the current
 * user's id, so user A and user B never share scheduling preferences on
 * the same browser. Mirrors Flutter's `key + userId` convention.
 */
const userScopedStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(`${name}.${currentUserKeySuffix()}`);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(`${name}.${currentUserKeySuffix()}`, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(`${name}.${currentUserKeySuffix()}`);
  },
};

export const useSchedulingStore = create<SchedulingState>()(
  persist(
    (set, get) => ({
      // Local components, not `toISOString()` — in Asia/Dubai (UTC+4) the UTC
      // date is still yesterday between 00:00 and 04:00 local, so the calendar
      // opened on the previous day for anyone starting early.
      selectedDate: todayLocalYmd(),
      activeView: 'Day',
      panelMode: null,
      selectedAppointmentId: null,
      selectedProviderIds: [],
      selectedSpecialties: [],
      selectedDeviceSubtypes: [],
      // Empty by default — the day-view shows every status except Cancelled,
      // which the filter helper hides on its own. Toggling a pill or picking
      // codes in the Statuses dropdown narrows to the explicit set.
      selectedStatusCodes: [],
      selectedInvoiceStatuses: [],
      selectedLocationIds: [],
      editTarget: null,

      setSelectedDate: (date) => set({ selectedDate: date }),
      setActiveView: (view) => set({ activeView: view }),
      openPanel: (mode, appointmentId = null) =>
        set({ panelMode: mode, selectedAppointmentId: appointmentId }),
      closePanel: () => set({ panelMode: null, selectedAppointmentId: null }),
      openEditPanel: (target) => set({ editTarget: target }),
      closeEditPanel: () => set({ editTarget: null }),

      setSelectedProviders: (ids) => set({ selectedProviderIds: ids }),
      toggleProvider: (id) => {
        const current = get().selectedProviderIds;
        set({
          selectedProviderIds: current.includes(id)
            ? current.filter((x) => x !== id)
            : [...current, id],
        });
      },
      clearProviders: () => set({ selectedProviderIds: [] }),

      setSelectedSpecialties: (s) => set({ selectedSpecialties: s }),
      toggleSpecialty: (s) => {
        const current = get().selectedSpecialties;
        set({
          selectedSpecialties: current.includes(s)
            ? current.filter((x) => x !== s)
            : [...current, s],
        });
      },
      clearSpecialties: () => set({ selectedSpecialties: [] }),

      toggleDeviceSubtype: (s) => {
        const current = get().selectedDeviceSubtypes;
        set({
          selectedDeviceSubtypes: current.includes(s)
            ? current.filter((x) => x !== s)
            : [...current, s],
        });
      },
      clearDeviceSubtypes: () => set({ selectedDeviceSubtypes: [] }),

      setSelectedStatusCodes: (codes) => set({ selectedStatusCodes: codes }),
      toggleStatusCode: (code) => {
        const current = get().selectedStatusCodes;
        set({
          selectedStatusCodes: current.includes(code)
            ? current.filter((x) => x !== code)
            : [...current, code],
        });
      },
      clearStatusCodes: () => set({ selectedStatusCodes: [] }),

      setSelectedInvoiceStatuses: (codes) =>
        set({ selectedInvoiceStatuses: codes }),
      toggleInvoiceStatus: (code) => {
        const current = get().selectedInvoiceStatuses;
        set({
          selectedInvoiceStatuses: current.includes(code)
            ? current.filter((x) => x !== code)
            : [...current, code],
        });
      },
      clearInvoiceStatuses: () => set({ selectedInvoiceStatuses: [] }),

      toggleLocation: (id) => {
        const current = get().selectedLocationIds;
        set({
          selectedLocationIds: current.includes(id)
            ? current.filter((x) => x !== id)
            : [...current, id],
        });
      },
      clearLocations: () => set({ selectedLocationIds: [] }),
    }),
    {
      name: 'scrips.scheduling',
      storage: {
        getItem: (name) => {
          // userScopedStorage is synchronous (backed by localStorage)
          // so the StateStorage `string | Promise<string | null>`
          // return narrows to string at runtime — cast.
          const raw = userScopedStorage.getItem(name) as string | null;
          return raw ? JSON.parse(raw) : null;
        },
        setItem: (name, value) =>
          userScopedStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => userScopedStorage.removeItem(name),
      },
      // `partialize` typed as full-state by Zustand, but the API is
      // specifically designed to return a SUBSET — cast.
      partialize: ((state: SchedulingState) => ({
        selectedSpecialties: state.selectedSpecialties,
        selectedDeviceSubtypes: state.selectedDeviceSubtypes,
        selectedProviderIds: state.selectedProviderIds,
        selectedStatusCodes: state.selectedStatusCodes,
        selectedInvoiceStatuses: state.selectedInvoiceStatuses,
        selectedLocationIds: state.selectedLocationIds,
      })) as (state: SchedulingState) => SchedulingState,
    },
  ),
);

// ---------------------------------------------------------------------------
// Auth-aware rehydration
// ---------------------------------------------------------------------------
// When the auth user changes (login / logout / refresh-induced switch), the
// localStorage key suffix changes too. Manually rehydrate so the in-memory
// state reflects the new user's persisted selections immediately, and reset
// to empty if the new user has none.
if (typeof window !== 'undefined') {
  let lastUserId: string | undefined = useAuthStore.getState().user?.userId;
  let lastPracticeId: string | undefined = useAuthStore.getState().user?.practiceId;
  useAuthStore.subscribe((state) => {
    const newUserId = state.user?.userId;
    const newPracticeId = state.user?.practiceId;
    // Practice switch, same user (the calendar's practice selector). Device
    // subtypes are per-practice free text — "chamber" at one practice and
    // "Tier B - Room" at another — so a filter carried across would silently
    // hide every column at the new practice (review #4). The practitioner
    // filters are deliberately untouched: provider ids are org-wide and the
    // Practitioners tab must behave exactly as it did.
    if (newUserId === lastUserId && newPracticeId !== lastPracticeId) {
      lastPracticeId = newPracticeId;
      useSchedulingStore.setState({ selectedDeviceSubtypes: [] });
      return;
    }
    if (newUserId === lastUserId) return;
    lastUserId = newUserId;
    lastPracticeId = newPracticeId;
    // Reset to defaults first (so a logout + new login doesn't briefly show
    // the previous user's selections), then rehydrate from the new key.
    useSchedulingStore.setState({
      selectedSpecialties: [],
      selectedDeviceSubtypes: [],
      selectedProviderIds: [],
      selectedStatusCodes: [],
      selectedInvoiceStatuses: [],
      selectedLocationIds: [],
      // Drop any open inline edit target on tenant/user switch so a stale
      // appointment id can never surface another tenant's appointment in the
      // edit rail (review: tenant-isolation).
      editTarget: null,
    });
    void useSchedulingStore.persist.rehydrate();
  });
}
